import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, Truck, AlertCircle } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ReservationForm } from "../components/forms/ReservationForm";
import { DevisForm } from "../components/forms/DevisForm";
import { ContactForm } from "../components/forms/ContactForm";
import { site } from "../config/site";

export default function Contact() {
  const location = useLocation();
  const [tab, setTab] = useState("devis");

  useEffect(() => {
    if (location.hash === "#reservation") setTab("reservation");
    else if (location.hash === "#devis") setTab("devis");
    else if (location.hash === "#contact") setTab("contact");
  }, [location.hash]);

  return (
    <main id="contenu-principal">
      <Seo
        title="Contact, réservation et devis traiteur | TN FOOD"
        description="Contactez TN FOOD à Sainte-Savine : réservez votre table, demandez un devis traiteur (réponse sous 48 h ouvrées) ou écrivez-nous. 34 Avenue du Général Gallieni — 07 75 77 29 51."
        path="/contact"
      />

      <section className="bg-bordeaux text-cream py-16 md:py-20">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <Reveal>
            <h1 className="font-display text-4xl md:text-5xl font-bold">Contact, réservation &amp; devis</h1>
            <p className="mt-4 text-lg text-cream/85 max-w-2xl">
              Une table à réserver, un événement à organiser ou une question ? Nous sommes à votre écoute.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl grid lg:grid-cols-5 gap-12">
          {/* Coordonnées */}
          <Reveal className="lg:col-span-2">
            <div className="space-y-8">
              <div>
                <h2 className="font-display text-2xl font-semibold text-encre mb-4">Nos coordonnées</h2>
                <ul className="space-y-4 text-taupe">
                  <li className="flex gap-3">
                    <MapPin className="h-5 w-5 mt-0.5 shrink-0 text-terracotta" strokeWidth={1.5} />
                    <span>{site.address.full}</span>
                  </li>
                  <li className="flex gap-3">
                    <Phone className="h-5 w-5 mt-0.5 shrink-0 text-terracotta" strokeWidth={1.5} />
                    <a href={site.phoneHref} data-testid="contact-phone" className="text-terracotta font-semibold hover:underline">{site.phone}</a>
                  </li>
                  <li className="flex gap-3">
                    <Mail className="h-5 w-5 mt-0.5 shrink-0 text-terracotta" strokeWidth={1.5} />
                    <a href={`mailto:${site.email}`} data-testid="contact-email" className="text-terracotta font-semibold hover:underline">{site.email}</a>
                  </li>
                  <li className="flex gap-3">
                    <Clock className="h-5 w-5 mt-0.5 shrink-0 text-terracotta" strokeWidth={1.5} />
                    <span><strong className="text-encre">Restaurant :</strong> {site.hours.restaurant}<br />
                    <strong className="text-encre">Traiteur :</strong> {site.hours.traiteur}</span>
                  </li>
                  <li className="flex gap-3">
                    <Truck className="h-5 w-5 mt-0.5 shrink-0 text-terracotta" strokeWidth={1.5} />
                    <span>
                      <strong className="text-encre">Livraison restaurant :</strong> {site.zones.livraison.join(", ")}.<br />
                      <strong className="text-encre">Zone traiteur :</strong> {site.zones.traiteur}.
                    </span>
                  </li>
                </ul>
              </div>

              <div className="rounded-2xl overflow-hidden border border-terracotta/15 shadow-sm">
                <iframe
                  title="Carte — TN FOOD, 34 Avenue du Général Gallieni, 10300 Sainte-Savine"
                  data-testid="contact-map"
                  src="https://www.openstreetmap.org/export/embed.html?bbox=4.028%2C48.288%2C4.068%2C48.305&layer=mapnik&marker=48.2966%2C4.0483"
                  className="w-full h-64 border-0"
                  loading="lazy"
                />
              </div>

              <p className="flex gap-3 text-sm text-taupe bg-cream border border-gold/40 rounded-xl p-4" data-testid="contact-allergenes">
                <AlertCircle className="h-5 w-5 mt-0.5 shrink-0 text-saffron" strokeWidth={1.5} />
                {site.allergenNotice}
              </p>
            </div>
          </Reveal>

          {/* Formulaires */}
          <Reveal delay={0.1} className="lg:col-span-3">
            <Tabs value={tab} onValueChange={setTab} data-testid="contact-tabs">
              <TabsList className="grid w-full grid-cols-3 bg-cream">
                <TabsTrigger value="reservation" data-testid="tab-reservation">Réservation</TabsTrigger>
                <TabsTrigger value="devis" data-testid="tab-devis">Devis traiteur</TabsTrigger>
                <TabsTrigger value="contact" data-testid="tab-contact">Contact</TabsTrigger>
              </TabsList>
              <div className="mt-8 bg-white rounded-2xl border border-terracotta/10 shadow-sm p-6 md:p-8">
                <TabsContent value="reservation">
                  <h2 className="font-display text-2xl font-semibold text-encre mb-6">Réserver une table</h2>
                  <ReservationForm />
                </TabsContent>
                <TabsContent value="devis">
                  <h2 className="font-display text-2xl font-semibold text-encre mb-2">Demande de devis traiteur</h2>
                  <p className="text-sm text-taupe mb-6">Réponse avec une proposition personnalisée sous 48 heures ouvrées.</p>
                  <DevisForm />
                </TabsContent>
                <TabsContent value="contact">
                  <h2 className="font-display text-2xl font-semibold text-encre mb-6">Nous écrire</h2>
                  <ContactForm />
                </TabsContent>
              </div>
            </Tabs>
            <p className="mt-4 text-sm text-taupe">
              Les informations transmises sont confidentielles et ne sont en aucun cas cédées à des tiers.
            </p>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
